import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';

const styles = {
    overlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    modal: {
        backgroundColor: '#fff', borderRadius: '8px',
        width: '100%', maxWidth: '600px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '90vh'
    },
    header: {
        padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    },
    title: { margin: 0, fontSize: '1.25rem', color: '#1e293b', fontWeight: 600 },
    closeBtn: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' },
    body: { padding: '20px', overflowY: 'auto', flex: 1 },
    row: { display: 'flex', gap: '16px', marginBottom: '16px' },
    col: { flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' },
    label: { marginBottom: '6px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' },
    input: {
        padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '4px',
        fontSize: '0.95rem', color: '#0f172a', width: '100%', boxSizing: 'border-box',
        backgroundColor: '#fff', outline: 'none'
    },
    dropdownContainer: { position: 'relative', width: '100%' },
    dropdownToggle: {
        padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '4px',
        backgroundColor: '#fff', textAlign: 'left', cursor: 'pointer',
        fontSize: '0.95rem', color: '#0f172a', width: '100%', boxSizing: 'border-box',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    },
    dropdownMenu: {
        position: 'absolute', top: '100%', left: 0, right: 0,
        marginTop: '4px', backgroundColor: '#fff', border: '1px solid #cbd5e1',
        borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        zIndex: 10001, maxHeight: '250px', overflowY: 'auto',
        display: 'flex', flexDirection: 'column'
    },
    searchInput: {
        padding: '12px 12px', border: 'none', borderBottom: '1px solid #cbd5e1',
        outline: 'none', width: '100%', boxSizing: 'border-box', fontSize: '0.9rem',
        position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 2
    },
    dropdownItem: {
        padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
        fontSize: '0.9rem', color: '#334155'
    },
    checkboxRow: {
        display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', marginBottom: '16px'
    },
    footer: {
        padding: '16px 20px', borderTop: '1px solid #e2e8f0',
        display: 'flex', justifyContent: 'flex-end', gap: '12px',
        backgroundColor: '#f8fafc', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px'
    },
    btnCancel: {
        padding: '8px 16px', background: 'transparent', border: '1px solid #cbd5e1',
        borderRadius: '4px', color: '#475569', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem'
    },
    btnSave: {
        padding: '8px 24px', background: '#2563eb', border: 'none',
        borderRadius: '4px', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem'
    }
};

const ManualAdhocModal = ({ open, onClose, onSuccess, initialMonth, initialYear }) => {
    const [staffList, setStaffList] = useState([]);
    const [elements, setElements] = useState([]);
    const [loading, setLoading] = useState(false);

    // Dropdown search states
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    const [formData, setFormData] = useState({
        staff_id: null,
        element_id: '',
        amount: '',
        payroll_month: initialMonth || new Date().getMonth() + 1,
        payroll_year: initialYear || new Date().getFullYear(),
        remarks: '',
        overrideExisting: false
    });

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];
    const years = [2025, 2026, 2027];

    useEffect(() => {
        if (open) {
            setFormData({ 
                staff_id: null,
                element_id: '',
                amount: '',
                remarks: '',
                overrideExisting: false,
                payroll_month: initialMonth || new Date().getMonth() + 1, 
                payroll_year: initialYear || new Date().getFullYear() 
            });
            fetchLookups();
            setSearchQuery('');
            setIsDropdownOpen(false);
        }
    }, [open, initialMonth, initialYear]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    const fetchLookups = async () => {
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const config = { headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant }, withCredentials: true };

            const [staffRes, elementsRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff`, config),
                axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements`, config)
            ]);

            if (Array.isArray(staffRes.data)) {
                setStaffList(staffRes.data);
            } else if (staffRes.data && staffRes.data.success) {
                setStaffList(staffRes.data.data);
            }
            if (elementsRes.data.success) {
                setElements(elementsRes.data.data);
            }
        } catch (error) {
            console.error("Error fetching lookups:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.staff_id || !formData.element_id || !formData.amount) {
            alert("Please fill required fields (Staff, Element, Amount)");
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            
            const payload = {
                ...formData,
                staff_id: formData.staff_id.staff_id,
                amount: parseFloat(formData.amount)
            };

            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/adhoc/manual`, payload, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });

            if (res.data.success) {
                if (onSuccess) onSuccess();
                onClose();
            }
        } catch (error) {
            console.error("Error saving manual adhoc:", error);
            alert("Error saving record. " + (error.response?.data?.message || ''));
        } finally {
            setLoading(false);
        }
    };

    const eligibleStaffList = useMemo(() => {
        const pMonth = parseInt(formData.payroll_month, 10);
        const pYear = parseInt(formData.payroll_year, 10);
        
        return staffList.filter(s => {
            const isCurrentlyEmployed = (s.employment_status !== 'RESIGNED' && s.employment_status !== 'TERMINATED') || s.display_status === 'NOTICE_PERIOD';
            if (isCurrentlyEmployed) {
                return true;
            }
            if (s.cessation_date) {
                const cDate = new Date(s.cessation_date);
                const cMonth = cDate.getMonth() + 1;
                const cYear = cDate.getFullYear();
                
                // Allow staff who ceased in the current payroll month or the previous month
                // This accounts for custom pay cycles (e.g. 26th to 25th) where someone resigning late in the previous month is still in this month's payroll.
                let thresholdMonth = pMonth - 1;
                let thresholdYear = pYear;
                if (thresholdMonth < 1) {
                    thresholdMonth = 12;
                    thresholdYear -= 1;
                }
                
                if (cYear > thresholdYear || (cYear === thresholdYear && cMonth >= thresholdMonth)) {
                    return true;
                }
            }
            return false;
        });
    }, [staffList, formData.payroll_month, formData.payroll_year]);

    const filteredStaff = useMemo(() => {
        if (!searchQuery) return eligibleStaffList;
        const q = searchQuery.toLowerCase();
        return eligibleStaffList.filter(s => 
            (s.full_name || '').toLowerCase().includes(q) || 
            (s.employee_number || '').toLowerCase().includes(q)
        );
    }, [eligibleStaffList, searchQuery]);

    if (!open) return null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Add Single Ad-Hoc Record</h2>
                    <button style={styles.closeBtn} onClick={onClose} type="button">&times;</button>
                </div>
                
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div style={styles.body}>
                        {/* Row 1: Staff Selection */}
                        <div style={styles.row}>
                            <div style={styles.col}>
                                <label style={styles.label}>Select Staff *</label>
                                <div style={styles.dropdownContainer} ref={dropdownRef}>
                                    <div 
                                        style={styles.dropdownToggle}
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    >
                                        <span>
                                            {formData.staff_id 
                                                ? `${formData.staff_id.employee_number} - ${formData.staff_id.full_name}`
                                                : 'Search and select staff...'}
                                        </span>
                                        <span style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>▼</span>
                                    </div>
                                    
                                    {isDropdownOpen && (
                                        <div style={styles.dropdownMenu}>
                                            <input 
                                                type="text" 
                                                autoFocus
                                                placeholder="Type name or ID to search..." 
                                                style={styles.searchInput}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            {filteredStaff.length > 0 ? (
                                                filteredStaff.map(staff => (
                                                    <div 
                                                        key={staff.staff_id} 
                                                        style={styles.dropdownItem}
                                                        onClick={() => {
                                                            setFormData({ ...formData, staff_id: staff });
                                                            setIsDropdownOpen(false);
                                                            setSearchQuery('');
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                    >
                                                        <strong>{staff.employee_number}</strong> - {staff.full_name}
                                                    </div>
                                                ))
                                            ) : (
                                                <div style={{ padding: '12px', color: '#94a3b8', fontSize: '0.9rem', textAlign: 'center' }}>
                                                    No staff found
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Row 2: Element & Amount */}
                        <div style={styles.row}>
                            <div style={styles.col}>
                                <label style={styles.label}>Select Element *</label>
                                <select 
                                    style={styles.input} 
                                    value={formData.element_id}
                                    onChange={(e) => setFormData({ ...formData, element_id: e.target.value })}
                                >
                                    <option value="" disabled>Select Element</option>
                                    {elements.map(el => (
                                        <option key={el.id} value={el.id}>
                                            {el.element_code} - {el.element_name} ({el.element_type})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.col}>
                                <label style={styles.label}>Amount *</label>
                                <input 
                                    type="number" 
                                    style={styles.input}
                                    placeholder="Enter amount"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Row 3: Remarks */}
                        <div style={styles.row}>
                            <div style={styles.col}>
                                <label style={styles.label}>Remarks</label>
                                <input 
                                    type="text" 
                                    style={styles.input}
                                    placeholder="Optional remarks"
                                    value={formData.remarks}
                                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Row 4: Payroll Period */}
                        <div style={styles.row}>
                            <div style={styles.col}>
                                <label style={styles.label}>Payroll Month</label>
                                <select 
                                    style={styles.input}
                                    value={formData.payroll_month}
                                    onChange={(e) => setFormData({ ...formData, payroll_month: e.target.value })}
                                >
                                    {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            <div style={styles.col}>
                                <label style={styles.label}>Payroll Year</label>
                                <select 
                                    style={styles.input}
                                    value={formData.payroll_year}
                                    onChange={(e) => setFormData({ ...formData, payroll_year: e.target.value })}
                                >
                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Checkbox */}
                        <div style={styles.checkboxRow}>
                            <input 
                                type="checkbox" 
                                id="overrideCb"
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                checked={formData.overrideExisting}
                                onChange={(e) => setFormData({ ...formData, overrideExisting: e.target.checked })}
                            />
                            <label htmlFor="overrideCb" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#dc2626', cursor: 'pointer' }}>
                                Override Existing Value (if exists for same Staff & Element)
                            </label>
                        </div>
                    </div>
                    
                    <div style={styles.footer}>
                        <button type="button" style={styles.btnCancel} onClick={onClose} disabled={loading}>
                            CANCEL
                        </button>
                        <button type="submit" style={styles.btnSave} disabled={loading}>
                            {loading ? 'SAVING...' : 'SAVE'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManualAdhocModal;
